import { useId, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, CircleDashed, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components';
import type { ChannexPublicationResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { PublicationSummary, PublicationTone } from './publicationSummary';

const TONE: Record<PublicationTone, { icon: ReactNode; className: string }> = {
  ok: { icon: <Check size={22} />, className: 'bg-sage/15 text-sage' },
  progress: {
    icon: <Loader2 size={22} className="motion-safe:animate-spin" />,
    className: 'bg-navy/10 text-navy',
  },
  neutral: { icon: <CircleDashed size={22} />, className: 'bg-card-alt text-ink-soft' },
  error: { icon: <AlertTriangle size={22} />, className: 'bg-sealwax/10 text-sealwax' },
};

interface Props {
  data: ChannexPublicationResponse;
  summary: PublicationSummary;
  /** Veröffentlichen oder Abgleichen des Objekts — nur, was die Rolle darf. */
  actions?: ReactNode;
}

/**
 * Wo das Objekt beim Kanalmanager steht. Die Kennung dort braucht nur, wer mit dem Support
 * spricht — sie liegt deshalb hinter „Technische Details" statt im Kopf.
 */
export function ObjectPublicationStatus({ data, summary, actions }: Props) {
  const [details, setDetails] = useState(false);
  const titleId = useId();
  const detailsId = useId();
  const tone = TONE[summary.tone];

  return (
    <section
      aria-labelledby={titleId}
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="flex flex-wrap items-start gap-4 p-5">
        <span
          aria-hidden
          className={cn('grid size-11 shrink-0 place-items-center rounded-full', tone.className)}
        >
          {tone.icon}
        </span>
        <div className="min-w-0 flex-1 basis-64">
          <h3
            id={titleId}
            className="text-ink @lg:text-[22px] font-serif text-[19px] leading-tight"
          >
            {summary.title}
          </h3>
          <p className="text-ink-soft mt-1 text-sm">{summary.text}</p>
          {data.parentLastError && (
            <p className="text-sealwax mt-1 text-sm">{data.parentLastError}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {data.channexPropertyId && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              aria-expanded={details}
              aria-controls={detailsId}
              onClick={() => setDetails((open) => !open)}
            >
              Technische Details
            </Button>
          )}
        </div>
      </div>
      {data.channexPropertyId && (
        <TechnicalDetails id={detailsId} hidden={!details} remoteId={data.channexPropertyId} />
      )}
    </section>
  );
}

function TechnicalDetails({
  id,
  hidden,
  remoteId,
}: {
  id: string;
  hidden: boolean;
  remoteId: string;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(remoteId);
      toast.success('Kennung kopiert');
    } catch {
      toast.error('Kopieren war nicht möglich. Die Kennung lässt sich auch markieren.');
    }
  };

  return (
    <div id={id} hidden={hidden} className="px-5 pb-5">
      <div className="border-line/60 bg-card-alt/40 @lg:grid-cols-[auto_minmax(0,1fr)_auto] grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-md border px-4 py-2">
        <span className="text-ink-soft @lg:col-span-1 col-span-2 text-[11px] font-semibold uppercase tracking-[0.08em]">
          Kennung beim Kanalmanager
        </span>
        <code className="text-ink select-all break-all font-mono text-xs">{remoteId}</code>
        <Button
          variant="ghost"
          size="sm"
          className="min-h-11"
          leftIcon={<Copy size={14} />}
          onClick={copy}
        >
          Kopieren
        </Button>
      </div>
    </div>
  );
}
